import React, { memo, useEffect, useMemo, useState } from 'react';
import { PurchaseHistoryTable, PurchaseHistoryColumn } from '../common/PurchaseHistoryTable';
import { StatusBadge, StatusTone, DateTimeCell, ItemsPreviewCell, LocationCell } from '../common/cells';
import {
  PurchaseHistoryRow,
  DateRangeValue,
  DateRangePreset,
  FacetGroup,
  FacetSelections,
  FulfillmentType,
  SegmentedFacetOption,
  RowActionMenuAction
} from '../common/types';
import { SearchInput } from '../common/SearchInput';
import { DateRangeFilter } from '../common/DateRangeFilter';
import { FilterDrawer, FilterTriggerButton } from '../common/FilterDrawer';
import { SegmentedFacetToggle } from '../common/SegmentedFacetToggle';
import { RowActionMenu } from '../common/RowActionMenu';
import { PaginationBar } from '../common/PaginationBar';
import { EmptyState, NoSearchResultsEmptyState } from '../common/EmptyState';
import { DEFAULT_ACTIVE_ORDERS_DATE_RANGE_PRESET_VALUE } from '../../data/dateRangePresets';

const SUPPORTED_SEARCH_FIELDS = ['Item description', 'Order number', 'PO/Job name'];

const STATUS_TONE: Record<string, StatusTone> = {
  Ready: 'success',
  'Out for Delivery': 'info'
};

// The generic mixed-fulfillment column set's own "Fulfillment" column — a complete map, not a
// DELIVERY/else binary, since FulfillmentType also includes LOCKER (a binary check would
// mislabel every LOCKER row as Pickup).
const FULFILLMENT_LABEL: Record<FulfillmentType, string> = {
  DELIVERY: 'Delivery',
  PICKUP: 'Pickup',
  LOCKER: 'Locker'
};

// View Order Details/Track Delivery are pure href links — RowActionMenu still requires an
// onSelect (it closes the menu on click regardless of href), but the action itself does nothing.
// One shared no-op, not a fresh closure per row per render, keeps each row's `actions` array
// entries referentially stable across renders where the row's own data hasn't changed.
const NOOP = () => {};

/**
 * Shape of the assembled request payload the connected ActiveOrdersTable builds by merging every
 * Active Orders toolbar control's emitted value. Field names match this component's controlled
 * props 1:1 — see ADR-003.
 */
export interface ActiveOrdersRequestPayload {
  // search: string; Suppressed until backend contract updates happen.
  dateRange: DateRangeValue;
  facets: FacetSelections;
  fulfillmentType: string;
  page: number;
  pageSize: number;
}

export interface ActiveOrdersTablePresentationalProps {
  rows: PurchaseHistoryRow[];
  isLoading?: boolean;
  isError?: boolean;
  onRetry?: () => void;
  /** Called when a row is clicked or activated via Enter; the host is responsible for navigating (e.g. to `row.viewDetailsUrl`). Omit to keep rows non-interactive. */
  onRowActivate?: (row: PurchaseHistoryRow) => void;
  /** Current search box value. Provide together with `onSearchSubmit` to show the search toolbar control. */
  searchValue?: string;
  /** Called with the trimmed search term on Enter or submit-button click; omit to hide the search control. */
  onSearchSubmit?: (value: string) => void;
  /** Label shown on the date filter trigger, e.g. "Pickup by". */
  dateRangeLabel?: string;
  /** Current date-range selection. Provide together with `dateRangePresets` and `onDateRangeApply` to show the date filter. */
  dateRangeValue?: DateRangeValue;
  /** Preset date ranges (e.g. Today, Tomorrow, Next 7 Days) offered by the date filter. */
  dateRangePresets?: DateRangePreset[];
  /** Called with the resolved range once a preset or valid custom range is applied; omit to hide the date filter. */
  onDateRangeApply?: (range: DateRangeValue) => void;
  /** Facet groups (e.g. Project, PO/Job Name) offered by the filter drawer. */
  facetGroups?: FacetGroup[];
  /** Called with the applied facet selections; omit together with `facetGroups` to hide the filter drawer. */
  onFacetsApply?: (selections: FacetSelections) => void;
  /** Shows skeleton rows in the filter drawer while facet options are being fetched. */
  isFacetsLoading?: boolean;
  /** Shows an error state in the filter drawer in place of the facet groups. */
  facetsError?: boolean;
  /** Deliveries/Pickups toggle options with counts. Provide together with `fulfillmentFilterValue` and `onFulfillmentFilterChange` to show the toggle. */
  fulfillmentFilterOptions?: SegmentedFacetOption[];
  /** Key of the currently active fulfillment-type toggle option ('DELIVERY' or 'PICKUP'). Also switches the table's column set and headers between the Deliveries and Pickups layouts; any other value (or omitting the prop) falls back to the generic mixed-fulfillment columns. */
  fulfillmentFilterValue?: string;
  /** Called with the selected option's key; the toggle only updates the host's request payload, it never triggers a fetch itself. */
  onFulfillmentFilterChange?: (key: string) => void;
  /** Total number of orders across all pages. Provide together with `page`, `pageSize`, and `onPageChange` to show the pagination bar. */
  totalItems?: number;
  /** Current 1-indexed page number. */
  page?: number;
  /** Number of rows per page. */
  pageSize?: number;
  /** Called with the next page number; the host is responsible for re-fetching `rows` for that page. */
  onPageChange?: (page: number) => void;
  /** Page-size choices (e.g. [10, 25, 50]) offered by the pagination bar's "N / Page" dropdown. Provide together with `onPageSizeChange` to show it. */
  pageSizeOptions?: number[];
  /** Called with the newly selected page size; the host is responsible for re-fetching `rows` at that size. */
  onPageSizeChange?: (pageSize: number) => void;
  /** Called with a row's order key (`${orderNumber}-${distributionType}`, matching this table's own `rowKey`) when its date cell is clicked; the host is responsible for opening a fulfillment drawer. Omit to keep date cells non-interactive. */
  onDateCellClick?: (orderKey: string) => void;
  /** Order key of the row whose fulfillment drawer is currently open, if any — reflected as `aria-expanded` on that row's date cell trigger. */
  openOrderKey?: string;
}

const ActiveOrdersTablePresentationalComponent = memo(
  ({
    rows,
    isLoading = false,
    isError = false,
    onRetry,
    onRowActivate,
    searchValue,
    onSearchSubmit,
    dateRangeLabel = 'Pickup by',
    dateRangeValue,
    dateRangePresets,
    onDateRangeApply,
    facetGroups,
    onFacetsApply,
    isFacetsLoading = false,
    facetsError = false,
    fulfillmentFilterOptions,
    fulfillmentFilterValue,
    onFulfillmentFilterChange,
    totalItems,
    page,
    pageSize,
    onPageChange,
    pageSizeOptions,
    onPageSizeChange,
    onDateCellClick,
    openOrderKey
  }: ActiveOrdersTablePresentationalProps) => {
    useEffect(() => {
      LIFE_CYCLE_EVENT_BUS.lifeCycle.trigger('active-orders-table.ready');
    }, []);

    const [isFilterDrawerOpen, setIsFilterDrawerOpen] = useState(false);

    const showSearch = Boolean(onSearchSubmit);
    const showDateRange = Boolean(dateRangePresets?.length && onDateRangeApply && dateRangeValue);
    const showFilter = Boolean(facetGroups?.length && onFacetsApply);
    const activeFacetCount = (facetGroups ?? []).reduce((sum, group) => sum + group.selectedValues.length, 0);
    const showFulfillmentFilter = Boolean(
      fulfillmentFilterOptions?.length && onFulfillmentFilterChange && fulfillmentFilterValue
    );
    const showPagination = Boolean(totalItems != null && page != null && pageSize && onPageChange);

    // Precedence mirrors AllOrdersTablePresentational/the reference Purchase History experience:
    // a live search term wins first, then an active date/facet filter narrowing an otherwise-
    // populated tab to zero, and only then the per-fulfillment-type "no orders" copy — so a
    // Deliveries tab that's genuinely empty reads "no deliveries", not a generic filter message.
    const hasSearchTerm = Boolean(searchValue?.trim());
    const hasActiveDateOrFacetFilter =
      Boolean(dateRangeValue?.preset && dateRangeValue.preset !== DEFAULT_ACTIVE_ORDERS_DATE_RANGE_PRESET_VALUE) ||
      activeFacetCount > 0;

    let emptyState: React.ReactNode;
    if (hasSearchTerm) {
      emptyState = (
        <NoSearchResultsEmptyState searchTerm={searchValue as string} supportedSearchFields={SUPPORTED_SEARCH_FIELDS} />
      );
    } else if (hasActiveDateOrFacetFilter) {
      emptyState = (
        <EmptyState
          headline="No active orders found for the selected filters."
          description="Try adjusting your date range or filters to see more orders."
        />
      );
    } else if (fulfillmentFilterValue === 'PICKUP') {
      emptyState = (
        <EmptyState
          headline="You don't have any pickups in progress right now."
          description="Orders ready for pickup will show up here."
        />
      );
    } else if (fulfillmentFilterValue === 'DELIVERY') {
      emptyState = (
        <EmptyState
          headline="You don't have any deliveries in progress right now."
          description="Orders on their way will show up here."
        />
      );
    } else {
      emptyState = <EmptyState headline="You don't have any active orders right now." />;
    }

    const toolbar =
      showSearch || showDateRange || showFilter || showFulfillmentFilter ? (
        <div className="sui-flex sui-w-full sui-flex-wrap sui-items-center sui-gap-3">
          {dateRangeValue && dateRangePresets?.length && onDateRangeApply && (
            <DateRangeFilter
              label={dateRangeLabel}
              value={dateRangeValue}
              presets={dateRangePresets}
              onApply={onDateRangeApply}
            />
          )}
          {showFulfillmentFilter && (
            <SegmentedFacetToggle
              options={fulfillmentFilterOptions as SegmentedFacetOption[]}
              activeKey={fulfillmentFilterValue as string}
              onChange={onFulfillmentFilterChange as (key: string) => void}
              groupLabel="Fulfillment type"
            />
          )}
          <div className="sui-ml-auto sui-flex sui-items-center sui-gap-3">
            {onSearchSubmit && (
              <div className="sui-w-72">
                <SearchInput value={searchValue} onSubmit={onSearchSubmit} ariaLabel="Search orders" />
              </div>
            )}
            {showFilter && (
              <FilterTriggerButton
                onClick={() => setIsFilterDrawerOpen(true)}
                activeCount={activeFacetCount}
                isOpen={isFilterDrawerOpen}
              />
            )}
          </div>
        </div>
      ) : undefined;

    const columns = useMemo<PurchaseHistoryColumn<PurchaseHistoryRow>[]>(() => {
      // Matches PurchaseHistoryTable's own `rowKey` below — the same composite identity
      // (see getActiveOrderKey in src/data/adapters/activeOrdersToRow.ts) is what ActiveOrdersTable
      // uses to look up the raw distribution for the fulfillment drawer.
      const getOrderKey = (row: PurchaseHistoryRow) => `${row.orderNumber}-${row.distributionType}`;
      const getDateCellOnClick = (row: PurchaseHistoryRow) =>
        onDateCellClick ? () => onDateCellClick(getOrderKey(row)) : undefined;
      const getDateCellIsOpen = (row: PurchaseHistoryRow) =>
        Boolean(onDateCellClick) && openOrderKey === getOrderKey(row);

      const actionsColumn: PurchaseHistoryColumn<PurchaseHistoryRow> = {
        key: 'actions',
        header: '',
        render: (row) => {
          // Real anchor hrefs (row.viewDetailsUrl/row.trackDeliveryUrl, built entirely from
          // activeOrders' own response — see buildActiveOrderDetailsPageUrl/buildTrackDeliveryUrl
          // in src/data/adapters/activeOrdersToRow.ts), not host-supplied callbacks — replicates
          // AllOrdersTable's own "View Order Details" decision (see ADR-002/ADR-003). Track
          // Delivery only appears when the row actually has one to offer (DELIVERY rows with a
          // trackingNumber) rather than linking to a page with nothing to track.
          const actions: RowActionMenuAction[] = [];
          if (row.viewDetailsUrl) {
            actions.push({ key: 'viewDetails', label: 'View Order Details', href: row.viewDetailsUrl, onSelect: NOOP });
          }
          if (row.trackDeliveryUrl) {
            actions.push({ key: 'trackDelivery', label: 'Track Delivery', href: row.trackDeliveryUrl, onSelect: NOOP });
          }
          return actions.length > 0 ? <RowActionMenu actions={actions} /> : null;
        }
      };

      if (fulfillmentFilterValue === 'PICKUP') {
        return [
          {
            key: 'pickupBy',
            header: 'Pickup By',
            render: (row) => (
              <DateTimeCell
                date={row.distributionDate}
                timeRange={row.distributionTime}
                onClick={getDateCellOnClick(row)}
                isOpen={getDateCellIsOpen(row)}
              />
            )
          },
          {
            key: 'items',
            header: 'Items',
            render: (row) => <ItemsPreviewCell items={row.lineItems} />
          },
          {
            key: 'status',
            header: 'Status',
            render: (row) => <StatusBadge label={row.status} tone={STATUS_TONE[row.status] ?? 'neutral'} />
          },
          {
            key: 'project',
            header: 'Project',
            render: (row) => row.projectName ?? '—'
          },
          {
            key: 'storeNumber',
            header: 'Store #',
            render: (row) => row.location.storeId ?? '—'
          },
          {
            key: 'storeAddress',
            header: 'Store Address',
            render: (row) => row.location.addressLine1 ?? row.location.storeName ?? '—'
          },
          {
            key: 'pickupPerson',
            header: 'Pickup Person',
            render: (row) => row.pickupPersonName ?? '—'
          },
          actionsColumn
        ];
      }

      if (fulfillmentFilterValue === 'DELIVERY') {
        return [
          {
            key: 'deliveryDate',
            header: 'Delivery Date',
            render: (row) => (
              <DateTimeCell
                date={row.distributionDate}
                timeRange={row.distributionTime}
                onClick={getDateCellOnClick(row)}
                isOpen={getDateCellIsOpen(row)}
              />
            )
          },
          {
            key: 'items',
            header: 'Items',
            render: (row) => <ItemsPreviewCell items={row.lineItems} />
          },
          {
            key: 'status',
            header: 'Status',
            render: (row) => <StatusBadge label={row.status} tone={STATUS_TONE[row.status] ?? 'neutral'} />
          },
          {
            key: 'project',
            header: 'Project',
            render: (row) => row.projectName ?? '—'
          },
          {
            key: 'poJob',
            header: 'PO/Job',
            render: (row) => row.poJobName ?? '—'
          },
          {
            key: 'address',
            header: 'Address',
            render: (row) => row.location.addressLine1 ?? '—'
          },
          {
            key: 'contactNumber',
            header: 'Contact #',
            render: (row) => row.contactNumber ?? '—'
          },
          actionsColumn
        ];
      }

      return [
        {
          key: 'orderDetails',
          header: 'Order Details',
          render: (row) => (
            <>
              <div className="sui-font-bold sui-text-primary">{row.orderNumber}</div>
              {row.projectName && <div className="sui-text-sm sui-text-subtle">{row.projectName}</div>}
            </>
          )
        },
        {
          key: 'fulfillment',
          header: 'Fulfillment',
          render: (row) => FULFILLMENT_LABEL[row.distributionType] ?? row.distributionType
        },
        {
          key: 'status',
          header: 'Status',
          render: (row) => <StatusBadge label={row.status} tone={STATUS_TONE[row.status] ?? 'neutral'} />
        },
        {
          key: 'dateTime',
          header: 'Date & Time',
          render: (row) => (
            <DateTimeCell
              date={row.distributionDate}
              timeRange={row.distributionTime}
              onClick={getDateCellOnClick(row)}
              isOpen={getDateCellIsOpen(row)}
            />
          )
        },
        {
          key: 'items',
          header: 'Items',
          render: (row) => <ItemsPreviewCell items={row.lineItems} />
        },
        {
          key: 'location',
          header: 'Location',
          render: (row) => (
            <LocationCell
              addressLine1={row.location.addressLine1}
              storeId={row.location.storeId}
              storeName={row.location.storeName}
            />
          )
        },
        actionsColumn
      ];
    }, [fulfillmentFilterValue, onDateCellClick, openOrderKey]);

    return (
      <>
        <PurchaseHistoryTable
          rowKey={(row) => `${row.orderNumber}-${row.distributionType}`}
          rows={rows}
          columns={columns}
          isLoading={isLoading}
          isError={isError}
          onRetry={onRetry}
          onRowClick={onRowActivate}
          emptyState={emptyState}
          toolbar={toolbar}
          footer={
            showPagination && (
              <PaginationBar
                totalItems={totalItems as number}
                page={page as number}
                pageSize={pageSize as number}
                onPageChange={onPageChange as (pageNumber: number) => void}
                pageSizeOptions={pageSizeOptions}
                onPageSizeChange={onPageSizeChange}
              />
            )
          }
        />
        {showFilter && (
          <FilterDrawer
            open={isFilterDrawerOpen}
            onClose={() => setIsFilterDrawerOpen(false)}
            facetGroups={facetGroups as FacetGroup[]}
            onApply={onFacetsApply as (selections: FacetSelections) => void}
            isLoading={isFacetsLoading}
            error={facetsError}
          />
        )}
      </>
    );
  }
);

ActiveOrdersTablePresentationalComponent.displayName = 'ActiveOrdersTablePresentational';

const ActiveOrdersTablePresentational = ActiveOrdersTablePresentationalComponent;

export { ActiveOrdersTablePresentational };
